"use client";

/**
 * The whole portal, before it knows who you are.
 *
 * `CreatorAuthGate` runs before the shell exists — it is deciding whether
 * there is a session at all — so its loading state cannot be the page
 * skeleton alone. It was a card in the middle of an empty page reading
 * "Loading NewForm One…", which is a different screen from the one arriving:
 * nothing is where it will be, and the app appears all at once when the
 * check resolves.
 *
 * THE CHROME IS THE REAL CHROME, not a drawing of it. The rail, the header
 * and the tab bar are the same components the shell mounts, because nothing
 * in them depends on the response: the same five destinations, in the same
 * order, with the same labels and icons every time. A skeleton of something
 * already known is a worse version of it, and it makes the nav flicker into
 * place for no reason.
 *
 * Only the two parts that do depend on data are drawn: the program control
 * and the account row, which the rail already renders as a line when it has
 * no address yet.
 *
 * It is inert while it shows. The destinations are real but the gate has not
 * resolved, so a press would either do nothing or race the redirect it is
 * about to perform; `pointer-events-none` is honest about that for the
 * moment it lasts.
 *
 * IT IS A SKELETON OF THE SIGNED-IN APP, and for a signed-out visitor the
 * sign-in screen replaces it a moment later. That is the trade every app
 * with persistent chrome makes, and it is the right way round here: almost
 * everyone opening this link has a session, and the cost of the rare wrong
 * guess is a brief flash, against a card that is wrong for everybody.
 *
 * The error states stay as messages. A skeleton says "this is arriving",
 * which is a lie when it is not.
 */

import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import type { CSSProperties } from "react";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { UpdatesSkeleton } from "./messaging/UpdatesSkeleton";
import {
  FOOTER_H,
  HEADER_H,
  RAIL_W,
  RAIL_W_COLLAPSED,
  TABBAR_H,
} from "./one/chrome";
import { HomeSkeleton } from "./one/HomeSkeleton";
import { SideRail } from "./one/SideRail";
import { TabBar } from "./one/TabBar";
import { TopBar } from "./one/TopBar";
import { tabForPath } from "./one/tabs";
import { useRailCollapsed } from "./one/useRailCollapsed";
import { RequestDetailSkeleton } from "./requests-view/RequestDetailSkeleton";
import { RequestsFeedPageSkeleton } from "./requests-view/RequestsFeedSkeleton";
import { ScriptReviewSkeleton } from "./ScriptReviewSkeleton";

/** The chrome is inert while the gate resolves — see the note above. */
const noop = () => {};

export function PortalSkeleton() {
  /*
    THE SHELL IS SHARED; THE SCREEN INSIDE IT IS NOT.

    This drew Home's bento on every route, so opening Script review showed a
    wall of cards for as long as the gate took and then replaced it with one
    centred field. A skeleton that is the wrong screen is worse than no
    skeleton: it is a specific, confident promise about a layout that is not
    coming.

    Routes without a screen of their own get the neutral body rather than
    Home's. Those screens each print their own heading and content, and a
    heading over a block is true of all of them, where a bento grid is true
    of exactly one.
  */
  const pathname = usePathname();
  const tab = tabForPath(pathname);
  // The request routes have two screens under one tab: the feed, and a
  // request of its own under /one/requests/<id>.
  const requestDetail = /^\/one\/requests\/[^/]+/.test(pathname);
  const [railCollapsed] = useRailCollapsed();

  return (
    <div
      style={
        {
          "--one-rail": railCollapsed ? RAIL_W_COLLAPSED : RAIL_W,
          "--one-tabbar": `${TABBAR_H}px`,
          "--one-header": `${HEADER_H}px`,
          "--one-inset": "env(safe-area-inset-bottom, 12px)",
        } as CSSProperties
      }
      className="min-h-screen bg-nf-ds-bg"
    >
      <div className="pointer-events-none fixed inset-y-0 left-0 z-20 hidden lg:flex">
        <SideRail
          // The route being loaded, not Home. The rail is the real component
          // here, so an active row that does not match the address is the
          // chrome contradicting the URL.
          tab={tab}
          onTab={noop}
          unread={0}
          unseen={0}
          switcher={<Skeleton className="h-[48px] w-full rounded-[12px]" />}
          email=""
          onSettings={noop}
          collapsed={railCollapsed}
          onCollapsedChange={noop}
        />
      </div>

      {/* The phone's chrome: a header strip and the tab bar, both at the
          heights the scroll area below already reserves for them. */}
      <div
        aria-hidden="true"
        style={{
          height: `calc(${HEADER_H}px + env(safe-area-inset-top, 0px))`,
        }}
        className="fixed inset-x-0 top-0 z-20 flex items-center px-nf-ds-6 lg:hidden"
      >
        <Skeleton className="h-[22px] w-[130px]" />
      </div>

      {/* The same column as `PortalShell`, down to the footer's height.
          `min-h-svh` with `main` as `flex-1` is what gives a centred screen
          something to centre in, and the reserved strip below is what keeps
          that space the same size here as it is once the real footer is
          under it. */}
      <div className="flex min-h-svh flex-col pt-[calc(var(--one-header)+env(safe-area-inset-top,0px))] pb-[calc(var(--one-tabbar)+var(--one-inset))] lg:pt-0 lg:pb-0 lg:pl-[var(--one-rail)]">
        <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-nf-ds-6 py-nf-ds-4 lg:py-nf-ds-8">
          {tab === "home" ? (
            <HomeSkeleton />
          ) : tab === "script" ? (
            <ScriptReviewSkeleton />
          ) : tab === "updates" ? (
            <UpdatesSkeleton />
          ) : tab === "requests" ? (
            requestDetail ? (
              <RequestDetailSkeleton />
            ) : (
              <RequestsFeedPageSkeleton />
            )
          ) : (
            <GenericScreenSkeleton />
          )}
        </main>

        {/* The footer's space, not the footer. Drawing skeleton bars for
            "Need a hand? Message us" would be inventing two links and an
            address we do not have yet; leaving the gap keeps the layout
            honest and still stops the page above it from sitting low. */}
        <div aria-hidden="true" style={{ height: FOOTER_H }} />
      </div>

      <div
        aria-hidden="true"
        style={{
          height: `calc(${TABBAR_H}px + env(safe-area-inset-bottom, 12px))`,
        }}
        className="fixed inset-x-0 bottom-0 z-20 flex items-center justify-around px-nf-ds-6 lg:hidden"
      >
        {["home", "requests", "videos", "submit", "inbox", "updates"].map(
          (key) => (
            <Skeleton key={key} className="size-nf-ds-8 rounded-nf-ds-lg" />
          ),
        )}
      </div>
    </div>
  );
}

/**
 * A heading and a block, for the screens with no skeleton of their own.
 *
 * Deliberately vague. Videos, Submit and Inbox all open with their own title
 * over their own content, so this is the part they share and the most that
 * can be claimed without claiming something false. Updates has a skeleton of
 * its own now and no longer lands here.
 */
function GenericScreenSkeleton() {
  const t = useTranslations("shell");
  return (
    <div className="flex flex-col gap-nf-ds-block">
      {/* Not `<output>`, see the note in `HomeSkeleton`. */}
      <span className="sr-only">{t("loading")}</span>
      <Skeleton className="h-[27px] w-[180px]" />
      <Skeleton className="h-[280px] w-full rounded-nf-ds-card" />
    </div>
  );
}
